import { createGlobalStyle } from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';

type ThemeType = 'dark' | 'light';

// Creamos un objeto para el tema actual
interface ThemeProps {
  themeMode: ThemeType;
}

// Exportamos un componente con estilos globales condicionales basados en el tema
const BaseGlobalStyles = createGlobalStyle<ThemeProps>`
  /* Variables CSS para ambos temas */
  :root {
    /* Variables para tema oscuro */
    --color-bg-dark: #030303;
    --color-text-dark: #ffffff;
    --color-primary-dark: #ac4949;
    --color-container-dark: rgba(20, 20, 20, 0.85);
    --color-elevated-dark: rgba(31, 31, 31, 0.9);
    --color-border-dark: rgba(172, 73, 73, 0.1);
    
    /* Variables para tema claro */
    --color-bg-light: #f0f2f5;
    --color-text-light: #333333;
    --color-primary-light: #ac4949;
    --color-container-light: rgba(255, 255, 255, 0.75);
    --color-elevated-light: rgba(255, 255, 255, 0.9);
    --color-border-light: rgba(172, 73, 73, 0.15);
  }

  /* Aplicar tema según la selección */
  body {
    background-color: ${props => props.themeMode === 'dark' ? 'var(--color-bg-dark)' : 'var(--color-bg-light)'};
    color: ${props => props.themeMode === 'dark' ? 'var(--color-text-dark)' : 'var(--color-text-light)'};
    transition: background-color 0.3s ease, color 0.3s ease;
  }

  /* Glassmorphism base styles */
  .glassmorphism-bg {
    position: relative;
    overflow: hidden;
    background-color: ${props => props.themeMode === 'dark' 
      ? 'rgba(3, 3, 3, 0.95)' 
      : 'rgba(240, 242, 245, 0.85)'};
    
    &::before {
      content: '';
      position: absolute;
      top: -50%;
      right: -50%;
      width: 100%;
      height: 100%;
      background: radial-gradient(
        circle, 
        ${props => props.themeMode === 'dark' 
          ? 'rgba(172, 73, 73, 0.15) 0%, rgba(172, 73, 73, 0) 70%' 
          : 'rgba(172, 73, 73, 0.08) 0%, rgba(172, 73, 73, 0) 70%'
        }
      );
      transform: rotate(-12deg);
      z-index: 0;
      pointer-events: none;
    }
    
    &::after {
      content: '';
      position: absolute;
      bottom: -50%;
      left: -50%;
      width: 100%;
      height: 100%;
      background: radial-gradient(
        circle, 
        ${props => props.themeMode === 'dark' 
          ? 'rgba(172, 73, 73, 0.1) 0%, rgba(172, 73, 73, 0) 70%' 
          : 'rgba(172, 73, 73, 0.05) 0%, rgba(172, 73, 73, 0) 70%'
        }
      );
      transform: rotate(15deg);
      z-index: 0;
      pointer-events: none;
    }
  }

  /* Solid effect for cards */
  .glassmorphism-card,
  .ant-card,
  .ant-modal-content,
  .ant-drawer-content,
  .glass-table {
    background-color: ${props => props.themeMode === 'dark'
      ? '#0a0a0a'
      : '#ffffff'} !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    border: none !important;
    position: relative;
    z-index: 2;
  }

  /* Layout styles */
  .ant-layout {
    background-color: ${props => props.themeMode === 'dark' ? 'var(--color-bg-dark)' : 'var(--color-bg-light)'} !important;
  }

  .ant-layout-header {
    background-color: ${props => props.themeMode === 'dark'
      ? 'rgba(3, 3, 3, 0.8)'
      : 'rgba(255, 255, 255, 0.8)'} !important;
    border-bottom: none !important;
    z-index: 1000;
  }

  /* Table styles */
  .ant-table {
    background-color: transparent !important;

    .ant-table-container {
      border-radius: 0;
      overflow: hidden;
    }

    .ant-table-content {
      background-color: ${props => props.themeMode === 'dark'
        ? '#0a0a0a'
        : '#ffffff'};
    }

    .ant-table-thead > tr > th {
      background-color: ${props => props.themeMode === 'dark'
        ? '#0a0a0a'
        : '#ffffff'} !important;
      color: ${props => props.themeMode === 'dark' ? '#fff' : '#333'};
      border-bottom: none !important;
    }

    .ant-table-tbody > tr > td {
      background-color: transparent !important;
      border-bottom: none !important;
    }

    .ant-table-row-expand-icon {
      color: ${props => props.themeMode === 'dark' ? '#ffffff' : '#000000'} !important;

      &:hover {
        color: ${props => props.themeMode === 'dark' ? '#ffffff' : '#000000'} !important;
      }
    }

    .ant-table-row-expand-icon-collapsed,
    .ant-table-row-expand-icon-expanded {
      color: ${props => props.themeMode === 'dark' ? '#ffffff' : '#000000'} !important;
    }

    .ant-table-tbody > tr:hover > td {
      background-color: ${props => props.themeMode === 'dark' 
        ? 'rgba(172, 73, 73, 0.1)' 
        : 'rgba(172, 73, 73, 0.05)'} !important;
    }

    .ant-table-tbody > tr.ant-table-row-selected > td {
      background-color: ${props => props.themeMode === 'dark' 
        ? 'rgba(172, 73, 73, 0.15)' 
        : 'rgba(172, 73, 73, 0.1)'} !important;
    }

    .ant-empty-normal {
      color: ${props => props.themeMode === 'dark' 
        ? 'rgba(255, 255, 255, 0.5)' 
        : 'rgba(0, 0, 0, 0.25)'};
    }
  }

  /* Pagination styles */
  .ant-pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 16px 0;
    
    .ant-pagination-prev,
    .ant-pagination-next,
    .ant-pagination-item,
    .ant-pagination-jump-prev,
    .ant-pagination-jump-next {
      background-color: ${props => props.themeMode === 'dark' 
        ? 'rgba(31, 31, 31, 0.7)' 
        : 'rgba(255, 255, 255, 0.7)'};
      border-color: ${props => props.themeMode === 'dark' 
        ? 'rgba(172, 73, 73, 0.1)' 
        : 'rgba(172, 73, 73, 0.15)'};
      
      a {
        color: ${props => props.themeMode === 'dark' ? '#fff' : '#333'};
      }
      
      &:hover {
        background-color: ${props => props.themeMode === 'dark' 
          ? 'rgba(172, 73, 73, 0.2)' 
          : 'rgba(172, 73, 73, 0.1)'};
        border-color: #ac4949;
      }
    }
    
    .ant-pagination-item-active {
      background-color: rgba(172, 73, 73, 0.8);
      border-color: #ac4949;
      
      a {
        color: #fff;
      }
      
      &:hover {
        background-color: #ac4949;
      }
    }
    
    .ant-pagination-options {
      .ant-select-selector {
        background-color: ${props => props.themeMode === 'dark' 
          ? 'rgba(31, 31, 31, 0.7)' 
          : 'rgba(255, 255, 255, 0.7)'} !important;
        border-color: ${props => props.themeMode === 'dark' 
          ? 'rgba(172, 73, 73, 0.1)' 
          : 'rgba(172, 73, 73, 0.15)'} !important;
            backdrop-filter: blur(10px);

        color: ${props => props.themeMode === 'dark' ? '#fff' : '#333'};
      }
      
      .ant-select-arrow {
        color: ${props => props.themeMode === 'dark' 
          ? 'rgba(255, 255, 255, 0.5)' 
          : 'rgba(0, 0, 0, 0.3)'};
      }
    }
  }

  /* Input styles */
  .ant-input, 
  .ant-input-password,
  .ant-select-selector,
  .ant-picker {
    background-color: ${props => props.themeMode === 'dark' 
      ? 'rgba(31, 31, 31, 0.7)' 
      : 'rgba(255, 255, 255, 0.7)'} !important;
    border: 1px solid ${props => props.themeMode === 'dark' 
      ? '#333' 
      : '#d9d9d9'} !important;
    color: ${props => props.themeMode === 'dark' ? '#fff' : '#333'} !important;
    
    &:hover, &:focus {
      border-color: rgba(172, 73, 73, 0.5) !important;
    }
  }

  /* Button styles */
  .ant-btn {
    
    &.ant-btn-primary {
      background-color: #ac4949;
      border-color: #ac4949;
      
      &:hover {
        background-color: #bc5959;
        border-color: #bc5959;
      }
    }
    
    &.ant-btn-default {
      background-color: ${props => props.themeMode === 'dark' 
        ? 'rgba(31, 31, 31, 0.7)' 
        : 'rgba(255, 255, 255, 0.7)'};
      border-color: ${props => props.themeMode === 'dark' ? '#333' : '#d9d9d9'};
      color: ${props => props.themeMode === 'dark' ? '#fff' : '#333'};
      
      &:hover {
        border-color: #ac4949;
        color: #ac4949;
      }
    }
  }
  
  /* Modal styles */
  .ant-modal-content {
    background-color: ${props => props.themeMode === 'dark'
      ? '#0a0a0a'
      : '#ffffff'} !important;
    border: none !important;
    box-shadow: none !important;

    .ant-modal-header {
      background-color: transparent !important;
      border-bottom: none !important;
      
      .ant-modal-title {
        color: ${props => props.themeMode === 'dark' ? '#fff' : '#333'} !important;
      }
    }
    
    .ant-modal-close {
      color: ${props => props.themeMode === 'dark' 
        ? 'rgba(255, 255, 255, 0.7)' 
        : 'rgba(0, 0, 0, 0.5)'} !important;
      
      &:hover {
        color: #ac4949 !important;
      }
    }
    
    .ant-modal-footer {
      border-top: none !important;
    }
  }
  
  /* Card styles */
  .ant-card {
    background-color: ${props => props.themeMode === 'dark'
      ? '#0a0a0a'
      : '#ffffff'} !important;
    border: none !important;
    box-shadow: none !important;

    .ant-card-head {
      background-color: transparent !important;
      border-bottom: none !important;

      .ant-card-head-title {
        color: ${props => props.themeMode === 'dark' ? '#fff' : '#333'} !important;
      }
    }
  }

  /* Drawer styles */
  .ant-drawer-content {
    background-color: ${props => props.themeMode === 'dark'
      ? '#0a0a0a'
      : '#ffffff'} !important;
    
    .ant-drawer-header {
      background-color: transparent;
      border-bottom: none !important;
    }
    
    .ant-drawer-title {
      color: ${props => props.themeMode === 'dark' ? '#fff' : '#333'};
    }
    
    .ant-drawer-close {
      color: ${props => props.themeMode === 'dark' 
        ? 'rgba(255, 255, 255, 0.7)' 
        : 'rgba(0, 0, 0, 0.5)'};
      
      &:hover {
        color: #ac4949;
      }
    }
  }

  /* Tooltip y Popover */
  .ant-tooltip-inner,
  .ant-popover-inner {
    background-color: ${props => props.themeMode === 'dark' 
      ? 'rgba(31, 31, 31, 0.9)' 
      : 'rgba(255, 255, 255, 0.9)'} !important;
    border: 1px solid ${props => props.themeMode === 'dark' 
      ? 'rgba(172, 73, 73, 0.1)' 
      : 'rgba(172, 73, 73, 0.15)'} !important;
    color: ${props => props.themeMode === 'dark' ? '#fff' : '#333'} !important;
  }

  /* Dropdown */
  .ant-dropdown-menu {
    background-color: ${props => props.themeMode === 'dark' 
      ? 'rgba(31, 31, 31, 0.9)' 
      : 'rgba(255, 255, 255, 0.9)'} !important;
    border: 1px solid ${props => props.themeMode === 'dark' 
      ? 'rgba(172, 73, 73, 0.1)' 
      : 'rgba(172, 73, 73, 0.15)'} !important;
    
    .ant-dropdown-menu-item:hover {
      background-color: ${props => props.themeMode === 'dark' 
        ? 'rgba(172, 73, 73, 0.2)' 
        : 'rgba(172, 73, 73, 0.1)'} !important;
    }
  }

  /* Select dropdown */
  .ant-select-dropdown {
    background-color: ${props => props.themeMode === 'dark' 
      ? 'rgba(31, 31, 31, 0.9)' 
      : 'rgba(255, 255, 255, 0.9)'} !important;
    
    .ant-select-item {
      color: ${props => props.themeMode === 'dark' ? '#fff' : '#333'};
      
      &:hover {
        background-color: ${props => props.themeMode === 'dark' 
          ? 'rgba(172, 73, 73, 0.2)' 
          : 'rgba(172, 73, 73, 0.1)'};
      }
      
      &-option-selected {
        background-color: ${props => props.themeMode === 'dark' 
          ? 'rgba(172, 73, 73, 0.4)' 
          : 'rgba(172, 73, 73, 0.2)'};
        color: ${props => props.themeMode === 'dark' ? '#fff' : '#333'};
      }
    }
  }

  /* Scrollbar personalizado */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  ::-webkit-scrollbar-track {
    background: ${props => props.themeMode === 'dark' 
      ? 'rgba(0, 0, 0, 0.2)' 
      : 'rgba(0, 0, 0, 0.05)'};
    border-radius: 0;
  }
  
  ::-webkit-scrollbar-thumb {
    background: ${props => props.themeMode === 'dark' 
      ? 'rgba(172, 73, 73, 0.3)' 
      : 'rgba(172, 73, 73, 0.2)'};
    border-radius: 0;
    
    &:hover {
      background: ${props => props.themeMode === 'dark' 
        ? 'rgba(172, 73, 73, 0.5)' 
        : 'rgba(172, 73, 73, 0.3)'};
    }
  }
  
  /* Switch de tema */
  .theme-switch {
    position: relative;
    width: 60px;
    height: 30px;
    background-color: ${props => props.themeMode === 'dark' 
      ? 'rgba(20, 20, 20, 0.9)' 
      : 'rgba(255, 255, 255, 0.9)'};
    border-radius: 0;
    border: 1px solid ${props => props.themeMode === 'dark' 
      ? 'rgba(172, 73, 73, 0.2)' 
      : 'rgba(172, 73, 73, 0.15)'};
    cursor: pointer;
    transition: all 0.3s ease;
    overflow: hidden;
    margin-right: 16px;
    
    &::before {
      content: '';
      position: absolute;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      top: 2px;
      left: ${props => props.themeMode === 'dark' ? '2px' : '32px'};
      background-color: ${props => props.themeMode === 'dark' 
        ? '#333' 
        : '#f0f2f5'};
      box-shadow: inset 8px -4px 0px 0px ${props => props.themeMode === 'dark' 
        ? '#000' 
        : '#ac4949'};
      transition: all 0.3s ease;
    }
    
    &:hover {
      border-color: #ac4949;
    }
  }
`;

// Componente que utiliza el contexto del tema para aplicar los estilos globales
const GlobalStyles: React.FC = () => {
  const { theme } = useTheme();
  return <BaseGlobalStyles themeMode={theme} />;
};

export default GlobalStyles; 